'use client'

import React from 'react'
import { Permission } from '@/lib/types'
import { User } from '@/lib/types'
import { useUserContext } from '@/components/providers'
import { hasPermission } from '@/lib/user/roles-and-permissions'
import { isUserAdmin } from '@/lib/user/utils'
import { useDeveloperFeatures, type DeveloperFeature } from '@/hooks/use-developer-feature'
import { Button } from '../ui/button'
import Link from 'next/link'

interface AccessGateProps {
    authIsRequired?: boolean  // auth required by default
    permission?: Permission | Permission[]
    developerFeature?: DeveloperFeature  // Only accepts specific feature names
    isDevelopmentMode?: boolean  // Only show in development mode
    authFallback?: React.ReactNode | 'dialog' | 'nothing'
    permissionFallback?: React.ReactNode
    children: React.ReactNode
}

// Unified component to handle auth, permissions, and developer features
export function AccessGate({
    authIsRequired = true,
    permission,
    developerFeature,
    isDevelopmentMode = false,
    authFallback = null,
    permissionFallback = null,
    children
}: AccessGateProps) {
    const { isUserAuthenticated, userContextValue, isAdmin } = useUserContext()
    const { isFeatureEnabled } = useDeveloperFeatures()
    
    // Development mode check - only show in development
    if (isDevelopmentMode && process.env.NEXT_PUBLIC_VERCEL_ENV !== 'development') {
        return null
    }
    
    // Authentication check
    if (authIsRequired && !isUserAuthenticated) {
        return <>{authFallback}</>
    }
    
    // Permission check using role-based permissions system  
    if (permission && userContextValue) {
        const checkPermission = (user: User, perm: Permission | Permission[]): boolean => {
            if (Array.isArray(perm)) {
                return perm.every(p => hasPermission(user, p))
            }
            return hasPermission(user, perm)
        }
        
        if (!checkPermission(userContextValue, permission)) {
            return <>{permissionFallback}</>
        }
    }
    
    // Developer feature check
    if (developerFeature) {
        if (!isAdmin || !isFeatureEnabled(developerFeature)) {
            return null
        }
    }
    
    // All checks passed, render children
    return <>{children}</>
}





export function RedirectToAuth() {
    return (
        <div className="flex flex-col items-center justify-center h-full w-full">
            <h1 className="text-2xl  mb-8">Authenticate to see this page</h1>
            <div className="flex gap-4">
                <Button variant="outline">
                    <Link href="/auth/log-in">
                        Login
                    </Link>
                </Button>
                <Button>
                    <Link href="/auth/sign-up">
                        Sign up
                    </Link>
                </Button>
            </div>
        </div>
    )
}